import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
    Activity, ChevronLeft, Search, RefreshCw, 
    Eye, FileText, Download, UserPlus, Edit, Trash2, Loader2, User, Users
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ActivityLogs() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [total, setTotal] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterAction, setFilterAction] = useState('all');
    const [filterUser, setFilterUser] = useState('all');
    const [filterClient, setFilterClient] = useState('all');
    const [limit, setLimit] = useState(50);
    const [users, setUsers] = useState([]);
    const [clients, setClients] = useState([]);

    useEffect(() => {
        if (user?.role !== 'admin') {
            navigate('/dashboard');
            return;
        }
        loadLogs();
    }, [user, limit, filterUser, filterClient]);

    async function loadLogs() {
        setLoading(true);
        try {
            let url = `${API_URL}/api/admin/activity-logs?limit=${limit}`;
            if (filterUser !== 'all') url += `&user_id=${filterUser}`;
            if (filterClient !== 'all') url += `&client_id=${filterClient}`;
            
            const res = await axios.get(url);
            setLogs(res.data.logs || []);
            setTotal(res.data.total || 0);
            setUsers(res.data.users || []);
            setClients(res.data.clients || []);
        } catch (e) {
            toast.error('Failed to load activity logs');
        } finally {
            setLoading(false);
        }
    }

    function formatDate(dateStr) {
        if (!dateStr) return 'Unknown';
        return new Date(dateStr).toLocaleString();
    }

    function formatAction(action) {
        const actionMap = {
            'viewed_client': { text: 'Viewed Client', color: 'bg-blue-100 text-blue-700', icon: Eye },
            'created_client': { text: 'Created Client', color: 'bg-green-100 text-green-700', icon: UserPlus },
            'updated_client': { text: 'Updated Client', color: 'bg-amber-100 text-amber-700', icon: Edit },
            'deleted_client': { text: 'Deleted Client', color: 'bg-red-100 text-red-700', icon: Trash2 },
            'generated_document': { text: 'Generated Document', color: 'bg-purple-100 text-purple-700', icon: FileText },
            'generated_multiple_documents': { text: 'Generated Documents', color: 'bg-purple-100 text-purple-700', icon: FileText },
            'exported_document_word': { text: 'Exported to Word', color: 'bg-indigo-100 text-indigo-700', icon: Download },
            'exported_document_pdf': { text: 'Exported to PDF', color: 'bg-indigo-100 text-indigo-700', icon: Download },
        };
        return actionMap[action] || { text: action?.replace(/_/g, ' ') || 'Unknown', color: 'bg-stone-100 text-stone-700', icon: Activity };
    }

    // Filter logs based on search and action filter
    const filteredLogs = logs.filter(log => {
        const matchesSearch = !searchTerm || 
            log.user_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            log.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            log.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            log.action?.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesAction = filterAction === 'all' || log.action === filterAction;
        
        return matchesSearch && matchesAction;
    });

    // Get unique actions for filter dropdown
    const uniqueActions = [...new Set(logs.map(l => l.action))].filter(Boolean).sort();

    // Calculate stats
    const stats = {
        clientViews: logs.filter(l => l.action === 'viewed_client').length,
        documentsGenerated: logs.filter(l => l.action?.includes('generated')).length,
        exports: logs.filter(l => l.action?.includes('exported')).length,
        edits: logs.filter(l => l.action?.includes('updated') || l.action?.includes('created')).length
    };

    if (user?.role !== 'admin') {
        return null;
    }

    return (
        <div className="min-h-screen bg-[#FDFCF8]" data-testid="activity-logs-page">
            {/* Header */}
            <header className="bg-white border-b border-stone-200 sticky top-0 z-40">
                <div className="flex items-center justify-between px-6 py-4 max-w-7xl mx-auto">
                    <div className="flex items-center gap-4">
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => navigate('/dashboard')}
                            className="rounded-none"
                            data-testid="back-button"
                        >
                            <ChevronLeft className="h-5 w-5" />
                        </Button>
                        <div className="flex items-center gap-3">
                            <Activity className="h-6 w-6 text-amber-600" />
                            <h1 className="font-serif text-xl font-semibold">User Activity Logs</h1>
                        </div>
                    </div>
                    <Button
                        variant="outline"
                        onClick={loadLogs}
                        disabled={loading}
                        className="rounded-none"
                        data-testid="refresh-button"
                    >
                        <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                        Refresh
                    </Button>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-6 py-8">
                {/* Stats */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <Card className="rounded-none border-stone-200">
                        <CardContent className="p-4">
                            <div className="text-2xl font-bold text-stone-900">{total}</div>
                            <div className="text-sm text-stone-500">Total Activities</div>
                        </CardContent>
                    </Card>
                    <Card className="rounded-none border-stone-200">
                        <CardContent className="p-4">
                            <div className="text-2xl font-bold text-blue-600">{stats.clientViews}</div>
                            <div className="text-sm text-stone-500">Client Views</div>
                        </CardContent>
                    </Card>
                    <Card className="rounded-none border-stone-200">
                        <CardContent className="p-4">
                            <div className="text-2xl font-bold text-purple-600">{stats.documentsGenerated}</div>
                            <div className="text-sm text-stone-500">Documents Generated</div>
                        </CardContent>
                    </Card>
                    <Card className="rounded-none border-stone-200">
                        <CardContent className="p-4">
                            <div className="text-2xl font-bold text-indigo-600">{stats.exports}</div>
                            <div className="text-sm text-stone-500">Document Exports</div>
                        </CardContent>
                    </Card>
                </div>

                {/* Filters */}
                <Card className="rounded-none border-stone-200 mb-6">
                    <CardContent className="p-4">
                        <div className="flex flex-col md:flex-row gap-4">
                            <div className="flex-1 relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
                                <Input
                                    placeholder="Search by user, client, or action..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-10 rounded-none"
                                    data-testid="search-input"
                                />
                            </div>
                            
                            {/* Filter by User */}
                            <div className="flex items-center gap-2">
                                <User className="h-4 w-4 text-stone-400" />
                                <select
                                    value={filterUser}
                                    onChange={(e) => setFilterUser(e.target.value)}
                                    className="h-10 px-3 border border-stone-200 bg-white rounded-none min-w-[180px]"
                                    data-testid="filter-user"
                                >
                                    <option value="all">All Users</option>
                                    {users.map(u => (
                                        <option key={u.id} value={u.id}>
                                            {u.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            
                            {/* Filter by Client */}
                            <div className="flex items-center gap-2">
                                <Users className="h-4 w-4 text-stone-400" />
                                <select
                                    value={filterClient}
                                    onChange={(e) => setFilterClient(e.target.value)}
                                    className="h-10 px-3 border border-stone-200 bg-white rounded-none min-w-[180px]"
                                    data-testid="filter-client"
                                >
                                    <option value="all">All Clients</option>
                                    {clients.map(c => (
                                        <option key={c.id} value={c.id}>
                                            {c.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            
                            {/* Filter by Action */}
                            <select
                                value={filterAction}
                                onChange={(e) => setFilterAction(e.target.value)}
                                className="h-10 px-3 border border-stone-200 bg-white rounded-none min-w-[180px]"
                                data-testid="filter-action"
                            >
                                <option value="all">All Actions</option>
                                {uniqueActions.map(action => (
                                    <option key={action} value={action}>
                                        {formatAction(action).text}
                                    </option>
                                ))}
                            </select>
                            
                            {/* Limit */}
                            <select
                                value={limit}
                                onChange={(e) => setLimit(Number(e.target.value))}
                                className="h-10 px-3 border border-stone-200 bg-white rounded-none"
                                data-testid="limit-select"
                            >
                                <option value={50}>Last 50</option>
                                <option value={100}>Last 100</option>
                                <option value={200}>Last 200</option>
                                <option value={500}>Last 500</option>
                            </select>
                        </div>
                    </CardContent>
                </Card>

                {/* Logs Table */}
                <Card className="rounded-none border-stone-200">
                    <CardHeader>
                        <CardTitle className="font-serif text-lg">
                            Activity Log ({filteredLogs.length} of {total})
                        </CardTitle>
                        <CardDescription>
                            Track what users are doing: viewing clients, generating documents, and more
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        {loading ? (
                            <div className="flex items-center justify-center py-12">
                                <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
                            </div>
                        ) : filteredLogs.length === 0 ? (
                            <div className="text-center py-12 text-stone-500">
                                No activity logs found
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full" data-testid="activity-logs-table">
                                    <thead>
                                        <tr className="border-b border-stone-200">
                                            <th className="text-left py-3 px-4 text-sm font-medium text-stone-500">Timestamp</th>
                                            <th className="text-left py-3 px-4 text-sm font-medium text-stone-500">User</th>
                                            <th className="text-left py-3 px-4 text-sm font-medium text-stone-500">Action</th>
                                            <th className="text-left py-3 px-4 text-sm font-medium text-stone-500">Client</th>
                                            <th className="text-left py-3 px-4 text-sm font-medium text-stone-500">Details</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredLogs.map((log, idx) => {
                                            const actionInfo = formatAction(log.action);
                                            const Icon = actionInfo.icon;
                                            
                                            return (
                                                <motion.tr
                                                    key={log.id || idx}
                                                    initial={{ opacity: 0 }}
                                                    animate={{ opacity: 1 }}
                                                    transition={{ delay: idx * 0.02 }}
                                                    className="border-b border-stone-100 hover:bg-stone-50"
                                                    data-testid={`activity-log-row-${idx}`}
                                                >
                                                    <td className="py-3 px-4 text-sm text-stone-600">
                                                        {formatDate(log.timestamp)}
                                                    </td>
                                                    <td className="py-3 px-4">
                                                        <div className="text-sm font-medium">{log.user_name || 'Unknown'}</div>
                                                        <div className="text-xs text-stone-500">{log.user_email}</div>
                                                    </td>
                                                    <td className="py-3 px-4">
                                                        <Badge className={`rounded-none text-xs ${actionInfo.color}`}>
                                                            <Icon className="h-3 w-3 mr-1" />
                                                            {actionInfo.text}
                                                        </Badge>
                                                    </td>
                                                    <td className="py-3 px-4 text-sm">
                                                        {log.client_name ? (
                                                            <span 
                                                                className="text-amber-700 hover:underline cursor-pointer"
                                                                onClick={() => log.client_id && navigate(`/clients/${log.client_id}`)}
                                                            >
                                                                {log.client_name}
                                                            </span>
                                                        ) : (
                                                            <span className="text-stone-400">—</span>
                                                        )}
                                                    </td>
                                                    <td className="py-3 px-4 text-sm text-stone-500">
                                                        {log.details ? (
                                                            <span className="text-xs">
                                                                {log.details.title && `"${log.details.title}"`}
                                                                {log.details.document_type && !log.details.title && log.details.document_type}
                                                                {log.details.count && `${log.details.count} documents`}
                                                            </span>
                                                        ) : (
                                                            <span className="text-stone-400">—</span>
                                                        )}
                                                    </td>
                                                </motion.tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </main>
        </div>
    );
}
